import AIChatWidget from '@/components/aichat/AiChatWidget';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { ChevronRight, Cookie } from 'lucide-react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

export default function CookiePolicy() {
    return (
        <LandingLayout>
            <SeoHead title="Cookie Policy" description="Cookie policy for Northeastern College's website." image={`${baseUrl}/images/logo.webp`} />

            <section id="cookie-policy" className="bg-muted pb-16">
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/cookie.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
                    <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                    <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-yellow-500/5 blur-[100px]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Cookie Policy</span>
                        </div>
                        
                        <h1 className="max-w-4xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Cookie Policy
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Understand how cookies support website functionality, preferences, and optional analytics.
                        </p>
                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    <div className="bg-card border-border overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-10">
                        <div className="mb-8 rounded-2xl border border-emerald-500/15 bg-emerald-500/5 px-5 py-4">
                            <p className="text-sm font-semibold tracking-wide text-[#0A7A35] uppercase">Last Updated</p>
                            <p className="text-foreground mt-1 text-lg font-bold">January 29, 2026</p>
                        </div>
                        <p className="text-muted-foreground mb-6 text-sm leading-relaxed md:text-base">
                            This Cookie Policy explains how Northeastern College uses cookies and similar technologies on our website.
                        </p>

                        <div className="text-muted-foreground space-y-6 text-sm leading-relaxed md:text-base">
                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">1. What Are Cookies?</h3>
                                <div>
                                    Cookies are small text files stored on your device when you visit a website. They help improve functionality and
                                    user experience.
                                </div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">2. Types of Cookies We Use</h3>
                                <div className="mb-2">We use the following categories of cookies:</div>
                                <div className="space-y-3">
                                    <div>
                                        <div className="text-foreground font-semibold">a. Essential Cookies</div>
                                        <ul className="list-disc space-y-1 pl-5">
                                            <li>Used for basic website operations</li>
                                            <li>Required for visitor logging and security</li>
                                            <li>Ensures certain pages load properly</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <div className="text-foreground font-semibold">b. Functional Cookies</div>
                                        <ul className="list-disc space-y-1 pl-5">
                                            <li>Remember basic preferences</li>
                                            <li>Improve navigation and usability</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <div className="text-foreground font-semibold">c. Analytics Cookies (optional, with consent)</div>
                                        <ul className="list-disc space-y-1 pl-5">
                                            <li>Used only after you give consent</li>
                                            <li>Help us understand how visitors use our website</li>
                                            <li>Collect anonymized usage data (e.g., pages visited, device type)</li>
                                            <li>Used solely to improve website performance and content</li>
                                        </ul>
                                    </div>
                                </div>
                                <div className="mt-3">We do NOT use:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>Advertising cookies</li>
                                    <li>Remarketing cookies</li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">3. How You Can Manage Cookies</h3>
                                <div>
                                    You may adjust your cookie preferences in our consent banner or disable cookies in your browser settings. However,
                                    disabling essential cookies may reduce site functionality.
                                </div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">4. Changes to This Cookie Policy</h3>
                                <div>We may update this policy from time to time and will revise the "Last Updated" date above.</div>
                            </div>

                            <div>
                                <h3 className="text-foreground mb-2 text-base font-semibold md:text-lg">5. Contact</h3>
                                <div>For questions:</div>
                                <div className="mt-1">support@northeasterncollege.edu.ph</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
